/**
 * Managed SQL Database Validation Schemas
 *
 * Validation schemas for managed SQL database management operations including
 * creation, updates, and access grants for applications and ELT jobs.
 */

import { z } from 'zod';
import { nonEmptyStringSchema, descriptionSchema, uuidSchema } from './common';

// ============================================================================
// MANAGED SQL DB CODE VALIDATION
// ============================================================================

/**
 * Managed SQL database code validation schema
 * Format: lowercase alphanumeric and hyphens only
 * Length: 2-30 characters
 * Must start with a letter, cannot end with hyphen
 * Examples: "prod", "staging", "analytics-db"
 */
export const managedSqlDbCodeSchema = z
  .string({
    required_error: 'Database code is required',
    invalid_type_error: 'Database code must be a string',
  })
  .min(2, 'Code must be at least 2 characters long')
  .max(30, 'Code must not exceed 30 characters')
  .regex(
    /^[a-z][a-z0-9-]*$/,
    'Code must start with a letter and contain only lowercase letters, numbers, and hyphens'
  )
  .refine(
    (code) => !code.endsWith('-'),
    'Code cannot end with a hyphen'
  );

export type ManagedSqlDbCodeValidation = z.infer<typeof managedSqlDbCodeSchema>;

// ============================================================================
// MANAGED SQL DB PERMISSION VALIDATION
// ============================================================================

/**
 * Valid managed SQL database permission values
 * Maps to PostgreSQL GRANT permissions
 */
export const managedSqlDbPermissionValues = [
  'Select',
  'Insert',
  'Update',
  'Delete',
  'Create',
  'Alter',
  'Drop',
  'Admin',
] as const;

/**
 * Managed SQL database permission validation schema
 */
export const managedSqlDbPermissionSchema = z.enum(managedSqlDbPermissionValues, {
  required_error: 'Permission is required',
  invalid_type_error: 'Invalid permission value',
});

export type ManagedSqlDbPermissionValidation = z.infer<typeof managedSqlDbPermissionSchema>;

/**
 * Managed SQL database permissions array validation schema
 */
export const managedSqlDbPermissionsArraySchema = z
  .array(managedSqlDbPermissionSchema)
  .min(1, 'At least one permission is required')
  .refine(
    (permissions) => new Set(permissions).size === permissions.length,
    'Duplicate permissions are not allowed'
  );

export type ManagedSqlDbPermissionsArrayValidation = z.infer<typeof managedSqlDbPermissionsArraySchema>;

// ============================================================================
// MANAGED SQL DB STATUS VALIDATION
// ============================================================================

/**
 * Valid managed SQL database status values
 */
export const managedSqlDbStatusValues = [
  'creating',
  'available',
  'updating',
  'deleting',
  'failed',
] as const;

/**
 * Managed SQL database status validation schema
 */
export const managedSqlDbStatusSchema = z.enum(managedSqlDbStatusValues, {
  invalid_type_error: 'Invalid status value',
});

export type ManagedSqlDbStatusValidation = z.infer<typeof managedSqlDbStatusSchema>;

// ============================================================================
// MANAGED SQL DB CREATE VALIDATION
// ============================================================================

/**
 * Managed SQL database creation schema
 * Used when creating a new managed SQL database for a tenant
 */
export const managedSqlDbCreateSchema = z
  .object({
    managedSqlDbCode: managedSqlDbCodeSchema,
    managedSqlDbName: nonEmptyStringSchema
      .min(1, 'Database name must be at least 1 character long')
      .max(100, 'Database name must not exceed 100 characters'),
    description: descriptionSchema,
  })
  .strict();

export type ManagedSqlDbCreateValidation = z.infer<typeof managedSqlDbCreateSchema>;

/**
 * Form-specific managed SQL database creation schema
 */
export const createManagedSqlDbFormSchema = z.object({
  managedSqlDbName: nonEmptyStringSchema
    .min(1, 'Database name is required')
    .max(100, 'Database name must not exceed 100 characters'),
  managedSqlDbCode: managedSqlDbCodeSchema,
  description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
});

export type CreateManagedSqlDbFormData = z.infer<typeof createManagedSqlDbFormSchema>;

// ============================================================================
// MANAGED SQL DB UPDATE VALIDATION
// ============================================================================

/**
 * Managed SQL database update schema
 * All fields are optional (at least one must be provided)
 * Note: managedSqlDbCode is immutable and cannot be updated
 */
export const managedSqlDbUpdateSchema = z
  .object({
    managedSqlDbName: nonEmptyStringSchema
      .min(1, 'Database name must be at least 1 character long')
      .max(100, 'Database name must not exceed 100 characters')
      .optional(),
    description: descriptionSchema.nullable(),
  })
  .strict()
  .refine(
    (data) => Object.keys(data).length > 0,
    'At least one field must be provided for update'
  );

export type ManagedSqlDbUpdateValidation = z.infer<typeof managedSqlDbUpdateSchema>;

/**
 * Form-specific managed SQL database update schema
 */
export const updateManagedSqlDbFormSchema = z.object({
  managedSqlDbName: nonEmptyStringSchema
    .min(1, 'Database name is required')
    .max(100, 'Database name must not exceed 100 characters'),
  description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
});

export type UpdateManagedSqlDbFormData = z.infer<typeof updateManagedSqlDbFormSchema>;

// ============================================================================
// APPLICATION GRANT VALIDATION
// ============================================================================

/**
 * Application access grant schema
 * Used when granting an application access to a managed SQL database
 */
export const managedSqlDbApplicationGrantSchema = z
  .object({
    applicationId: uuidSchema,
    permissions: managedSqlDbPermissionsArraySchema,
  })
  .strict();

export type ManagedSqlDbApplicationGrantValidation = z.infer<typeof managedSqlDbApplicationGrantSchema>;

/**
 * Form-specific application access grant schema
 */
export const grantApplicationAccessFormSchema = z.object({
  applicationId: uuidSchema,
  permissions: managedSqlDbPermissionsArraySchema,
});

export type GrantApplicationAccessFormData = z.infer<typeof grantApplicationAccessFormSchema>;

// ============================================================================
// ELT JOB GRANT VALIDATION
// ============================================================================

/**
 * ELT job access grant schema
 * Used when granting an ELT job access to a managed SQL database
 */
export const managedSqlDbEltJobGrantSchema = z
  .object({
    eltJobId: uuidSchema,
    permissions: managedSqlDbPermissionsArraySchema,
  })
  .strict();

export type ManagedSqlDbEltJobGrantValidation = z.infer<typeof managedSqlDbEltJobGrantSchema>;

/**
 * Form-specific ELT job access grant schema
 */
export const grantEltJobAccessFormSchema = z.object({
  eltJobId: uuidSchema,
  permissions: managedSqlDbPermissionsArraySchema,
});

export type GrantEltJobAccessFormData = z.infer<typeof grantEltJobAccessFormSchema>;

// ============================================================================
// MANAGED SQL DB QUERY VALIDATION
// ============================================================================

/**
 * Managed SQL database list query parameters schema
 */
export const managedSqlDbListQuerySchema = z
  .object({
    status: managedSqlDbStatusSchema.optional(),
    limit: z
      .string()
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().min(1).max(100))
      .optional(),
    lastKey: z.string().optional(),
  })
  .strict();

export type ManagedSqlDbListQueryValidation = z.infer<typeof managedSqlDbListQuerySchema>;
